import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';




// Cashback utilise Render (tiktok-live-connector) via Edge Function check-live-cashback.
// Plus rapide pour des gros batchs (10-20 concurrent), tolere quelques faux negatifs.
// Recruitment (admin/manager) utilise check-live (tikwm) qui est 100% precis mais 1 req/s.
const LIVE_CHECK_API = 'https://oqaidhzkuzjourwbqwyr.supabase.co/functions/v1/check-live-cashback';

const searchInputStyle = { flex: 1, minWidth: '200px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '13px', outline: 'none' };
const StableSearchInput = React.memo(({ onChange, placeholder }) => (
  <input
    placeholder={placeholder}
    onChange={e => onChange(e.target.value)}
    style={searchInputStyle}
  />
));

const formatPeriod = (locale, period) => {
  if (!period) return '—';
  const [year, month] = period.split('-');
  return new Date(year, month - 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

const formatPeriodShort = (locale, period) => {
  if (!period) return '—';
  const [year, month] = period.split('-');
  return new Date(year, month - 1).toLocaleDateString(locale, { month: 'short', year: 'numeric' });
};

const formatNumber = (locale, n) => {
  if (n == null) return '0';
  return Number(n).toLocaleString(locale);
};

const getCurrentPeriod = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const CashbackPayments = () => {
  const { t, i18n } = useTranslation();
  const localeMap = { fr: 'fr-FR', en: 'en-US', it: 'it-IT', de: 'de-DE' };
  const currentLocale = localeMap[i18n.language] || 'en-US';
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [accessChecked, setAccessChecked] = useState(false);
  const [userRole, setUserRole] = useState('');

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [periodFilter, setPeriodFilter] = useState('');
  const [historyPeriodFilter, setHistoryPeriodFilter] = useState('');
  const [historyView, setHistoryView] = useState('period'); // 'period' | 'creator'
  const [searchQuery, setSearchQuery] = useState('');
  const [allSearchQuery, setAllSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'paid' | 'pending' | 'partial'
  const [hideDeparted, setHideDeparted] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [validationModal, setValidationModal] = useState(null); // payment object or null
  const [validationAmount, setValidationAmount] = useState('');
  const [periodStatuses, setPeriodStatuses] = useState({}); // { period: 'active'|'on_hold' }
  const [togglingPeriod, setTogglingPeriod] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  // Access control
  useEffect(() => {
    const checkAccess = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login-authentication'); return; }
      setUserEmail(user.email || '');
      const { data: userData } = await supabase
        .from('users')
        .select('is_paid, name, role')
        .eq('id', user.id)
        .single();
      if (!userData?.is_paid) { navigate('/admin/dashboard'); return; }
      setCurrentUserName(userData?.name || user.email || '');
      setUserRole(userData?.role || '');
      setAccessChecked(true);
    };
    checkAccess();
  }, [navigate]);

  const fetchPayments = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    const { data } = await supabase
      .from('cashback_payments')
      .select('*, display_name')
      .order('rappel_count', { ascending: false })
      .order('created_at', { ascending: true });
    // Fetch creators who left the agency
    const { data: departedCreators } = await supabase
      .from('creators')
      .select('tiktok_id')
      .eq('agency_status', 'left_agency');
    const inactiveSet = new Set((departedCreators || []).map(c => c.tiktok_id));
    const enriched = (data || []).map(p => ({ ...p, is_departed: inactiveSet.has(p.tiktok_id) }));
    setPayments(enriched);
    setLoading(false);
  }, []);

  const fetchPeriodStatuses = useCallback(async () => {
    const { data } = await supabase.from('cashback_period_status').select('*');
    const map = {};
    (data || []).forEach(r => { map[r.period] = r.status; });
    setPeriodStatuses(map);
  }, []);

  useEffect(() => {
    if (accessChecked) {
      fetchPayments(true);
      fetchPeriodStatuses();
    }
  }, [accessChecked, fetchPayments, fetchPeriodStatuses]);

  // Set default period filter to most recent
  useEffect(() => {
    if (payments.length > 0 && !periodFilter) {
      const periods = [...new Set(payments.map(p => p.period))].filter(Boolean).sort().reverse();
      if (periods.length > 0) setPeriodFilter(periods[0]);
    }
  }, [payments]);

  const periods = [...new Set(payments.map(p => p.period))].filter(Boolean).sort().reverse();

  const pending = payments.filter(p => p.status === 'pending' && periodStatuses[p.period] !== 'on_hold');
  const paid = payments.filter(p => p.status === 'paid');

  // Live tab: only is_live = true creators, respecting hideDeparted toggle
  const liveCreators = pending.filter(p => p.is_live === true && (!hideDeparted || !p.is_departed));

  const departedCount = pending.filter(p => p.is_departed).length;

  // Detect creators with multiple pending periods (for visual grouping)
  const multiPeriodCreators = (() => {
    const countByCreator = {};
    pending.forEach(p => {
      const key = p.tiktok_id || p.username;
      if (!countByCreator[key]) countByCreator[key] = [];
      countByCreator[key].push(p.period);
    });
    return new Set(Object.keys(countByCreator).filter(k => countByCreator[k].length > 1));
  })();

  // Period colors for multi-period distinction
  const periodColors = ['#8b5cf6', '#3b82f6', '#f59e0b', '#ec4899', '#06b6d4'];
  const periodColorMap = (() => {
    const sorted = [...new Set(pending.map(p => p.period))].filter(Boolean).sort();
    const map = {};
    sorted.forEach((p, i) => { map[p] = periodColors[i % periodColors.length]; });
    return map;
  })();

  const filteredPending = pending.filter(p => {
    const matchPeriod = !periodFilter || p.period === periodFilter;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      (p.username || '').toLowerCase().includes(q) ||
      (p.display_name || '').toLowerCase().includes(q) ||
      (p.tiktok_id || '').toLowerCase().includes(q) ||
      (p.whatsapp || '').toLowerCase().includes(q);
    const matchDeparted = !hideDeparted || !p.is_departed;
    return matchPeriod && matchSearch && matchDeparted;
  });

  const sortedPending = [...filteredPending].sort((a, b) => {
    // Departed at the bottom
    if (a.is_departed && !b.is_departed) return 1;
    if (!a.is_departed && b.is_departed) return -1;
    if (a.is_live && !b.is_live) return -1;
    if (!a.is_live && b.is_live) return 1;
    return (b.rappel_count || 0) - (a.rappel_count || 0);
  });

  // KPIs
  const kpiPendingCount = pending.length;
  const kpiPendingAmount = pending.reduce((s, p) => s + (parseFloat(p.cashback_amount) || 0), 0);
  const kpiPendingDiamonds = Math.round(kpiPendingAmount / 0.01);
  const kpiPaidCount = paid.length;

  // FIX 1: totalPaidDiamonds
  const totalPaidDiamonds = Math.round(
    payments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + (parseFloat(p.cashback_amount) || 0), 0)
    / 0.01
  );

  // Historique grouped by period
  const historyByPeriod = {};
  payments.forEach(p => {
    if (!p.period) return;
    if (!historyByPeriod[p.period]) historyByPeriod[p.period] = { period: p.period, total: 0, paidCount: 0, totalAmount: 0 };
    historyByPeriod[p.period].total += 1;
    if (p.status === 'paid') historyByPeriod[p.period].paidCount += 1;
    historyByPeriod[p.period].totalAmount += parseFloat(p.cashback_amount) || 0;
  });
  const allHistoryRows = Object.values(historyByPeriod).sort((a, b) => b.period.localeCompare(a.period));
  const historyRows = historyPeriodFilter
    ? allHistoryRows.filter(r => r.period === historyPeriodFilter)
    : allHistoryRows;

  // Par créateur view — ALL payments (paid + pending) with status badge
  const allPaymentsByCreator = [...payments]
    .sort((a, b) => {
      // Paid first, then pending
      if (a.status === 'paid' && b.status !== 'paid') return -1;
      if (a.status !== 'paid' && b.status === 'paid') return 1;
      return (b.paid_at || b.created_at || '').localeCompare(a.paid_at || a.created_at || '');
    });

  const filteredAllPayments = allPaymentsByCreator.filter(p => {
    const matchPeriod = !historyPeriodFilter || p.period === historyPeriodFilter;
    const q = allSearchQuery.toLowerCase();
    const matchSearch = !q ||
      (p.username || '').toLowerCase().includes(q) ||
      (p.display_name || '').toLowerCase().includes(q) ||
      (p.tiktok_id || '').toLowerCase().includes(q) ||
      (p.whatsapp || '').toLowerCase().includes(q);
    const isPaid = p.status === 'paid';
    const isPartial = !isPaid && (p.amount_paid_diamonds || 0) > 0;
    const matchStatus = !statusFilter ||
      (statusFilter === 'paid' && isPaid) ||
      (statusFilter === 'pending' && !isPaid && !isPartial) ||
      (statusFilter === 'partial' && isPartial);
    const matchDeparted = !hideDeparted || !p.is_departed;
    return matchPeriod && matchSearch && matchStatus && matchDeparted;
  });

  const checkLiveStatus = async () => {
    setIsChecking(true);
    try {
      // Optimisation: exclure les departs (is_departed) + seulement pending
      const allPendingPayments = payments.filter(p => p.status === 'pending' && !p.is_departed);

      // Deduplicate by username
      const usernameToPaymentIds = {};
      allPendingPayments.forEach(p => {
        const username = (p.username || '').replace(/^@/, '').trim();
        if (username) {
          if (!usernameToPaymentIds[username]) usernameToPaymentIds[username] = [];
          usernameToPaymentIds[username].push(p.id);
        }
      });

      const usernames = Object.keys(usernameToPaymentIds);
      if (usernames.length === 0) { setIsChecking(false); return; }

      // Batch check via VPS API (real-time TikTok check)
      const res = await fetch(LIVE_CHECK_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users: usernames }),
      });
      const results = await res.json();

      const liveSet = new Set(results.filter(r => r.isLive).map(r => r.username));

      // Reset all is_live to false
      await supabase.from('cashback_payments').update({ is_live: false }).eq('status', 'pending');

      // Mark live creators
      const livePaymentIds = [];
      for (const [username, paymentIds] of Object.entries(usernameToPaymentIds)) {
        if (liveSet.has(username)) livePaymentIds.push(...paymentIds);
      }

      if (livePaymentIds.length > 0) {
        for (let i = 0; i < livePaymentIds.length; i += 50) {
          const batch = livePaymentIds.slice(i, i + 50);
          await supabase.from('cashback_payments').update({ is_live: true }).in('id', batch);
        }
      }

      await fetchPayments();
      showToast(`${livePaymentIds.length} ${t('admin.cashback.liveDetected', 'créateur(s) en live détecté(s)')}`);
    } catch (err) {
      console.error('Check live error:', err);
      showToast('Erreur vérification lives', 'error');
    }
    setIsChecking(false);
  };

  const openValidationModal = (payment) => {
    const cashbackDiamonds = Math.round((parseFloat(payment.cashback_amount) || 0) / 0.01);
    const alreadyPaid = payment.amount_paid_diamonds || 0;
    const remaining = cashbackDiamonds - alreadyPaid;
    setValidationAmount(String(remaining));
    setValidationModal({ ...payment, _cashbackDiamonds: cashbackDiamonds, _alreadyPaid: alreadyPaid, _remaining: remaining });
  };

  const generateSuggestions = (amount) => {
    if (!amount || amount <= 0) return [];
    const suggestions = [];
    // Arrondi supérieur aux 1000
    const roundUp1k = Math.ceil(amount / 1000) * 1000;
    if (roundUp1k !== amount) suggestions.push(roundUp1k);
    // Arrondi supérieur aux 5000
    const roundUp5k = Math.ceil(amount / 5000) * 5000;
    if (roundUp5k !== amount && roundUp5k !== roundUp1k) suggestions.push(roundUp5k);
    // Arrondi supérieur aux 10000
    const roundUp10k = Math.ceil(amount / 10000) * 10000;
    if (roundUp10k !== amount && roundUp10k !== roundUp5k && roundUp10k !== roundUp1k) suggestions.push(roundUp10k);
    // Arrondi inférieur aux 1000 (pour paiement partiel)
    const roundDown1k = Math.floor(amount / 1000) * 1000;
    if (roundDown1k > 0 && roundDown1k !== amount) suggestions.push(roundDown1k);
    return [...new Set(suggestions)].sort((a, b) => a - b);
  };

  const confirmValidation = async () => {
    if (!validationModal) return;
    const payment = validationModal;
    const amountGiven = parseInt(validationAmount) || 0;
    if (amountGiven <= 0) return;

    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    const now = new Date().toISOString();

    const newTotalPaid = (payment._alreadyPaid || 0) + amountGiven;
    const isFullyPaid = newTotalPaid >= payment._cashbackDiamonds;

    // Append to payment_history
    const prevHistory = Array.isArray(payment.payment_history) ? payment.payment_history : [];
    const newHistory = [...prevHistory, {
      amount_diamonds: amountGiven,
      paid_at: now,
      paid_by_name: currentUserName
    }];

    const updateData = {
      amount_paid_diamonds: newTotalPaid,
      payment_history: newHistory,
      is_live: false
    };

    if (isFullyPaid) {
      updateData.status = 'paid';
      updateData.paid_by_id = userId;
      updateData.paid_by_name = currentUserName;
      updateData.paid_at = now;
    }

    await supabase
      .from('cashback_payments')
      .update(updateData)
      .eq('id', payment.id);

    if (isFullyPaid) {
      showToast(`✓ ${t('admin.cashback.cashbackValidated', 'Cashback validated for')} ${payment.username} — ${formatNumber(currentLocale,newTotalPaid)} 💎`);
    } else {
      const reste = payment._cashbackDiamonds - newTotalPaid;
      showToast(`✓ ${t('admin.cashback.partialPaymentToast', 'Partial payment')} ${formatNumber(currentLocale,amountGiven)} 💎 ${t('admin.cashback.forCreator', 'for')} ${payment.username} — ${t('admin.cashback.remainingShort', 'Remaining')}: ${formatNumber(currentLocale,reste)} 💎`);
    }

    setValidationModal(null);
    fetchPayments();
  };

  const togglePeriodStatus = async (period, currentStatus) => {
    setTogglingPeriod(period);
    const newStatus = currentStatus === 'on_hold' ? 'active' : 'on_hold';
    await supabase
      .from('cashback_period_status')
      .upsert({ period, status: newStatus, updated_at: new Date().toISOString(), updated_by: currentUserName }, { onConflict: 'period' });
    await fetchPeriodStatuses();
    setTogglingPeriod(null);
    showToast(`${t('admin.cashback.periodHeader', 'Period')} ${formatPeriod(currentLocale,period)} → ${newStatus === 'on_hold' ? t('admin.cashback.statusOnHold', 'On hold') : t('admin.cashback.statusActive', 'Active')}`);
  };

  const getTierColor = (tierM1, tierCurrent) => {
    if (!tierM1 || !tierCurrent) return 'rgba(255,255,255,0.5)';
    if (tierCurrent > tierM1) return '#22c55e';
    if (tierCurrent === tierM1) return '#3b82f6';
    return '#ef4444';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  if (!accessChecked) {
    return (
      <div style={{ background: '#0d0d0d', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>{t('admin.cashback.accessCheck', 'Vérification des accès...')}</div>
      </div>
    );
  }

  const isAdmin = userRole === 'admin';

  const cashbackContent = (
    <main style={{ flex: 1, overflowY: 'auto', padding: '24px', paddingBottom: isAdmin ? '24px' : '80px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#fff', fontFamily: 'Sora, sans-serif', margin: 0 }}>
          {t('admin.cashback.paymentsTitle', 'Paiements Cashback')}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px', marginTop: '4px' }}>
          {t('admin.cashback.paymentsSubtitle', 'Gestion des paiements cashback créateurs')}
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{t('admin.cashback.totalPending', 'Total en attente')}</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#fff', fontFamily: 'Sora, sans-serif' }}>{kpiPendingCount}</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{t('admin.cashback.payments', 'paiements')}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{t('admin.cashback.totalPendingAmount', 'Total $ en attente')}</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
            ${kpiPendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{t('admin.cashback.cashbackUsd', 'cashback USD')}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{t('admin.cashback.totalPendingDiamonds', 'Total 💎 en attente')}</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
            💎 {kpiPendingDiamonds.toLocaleString(currentLocale)}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{t('admin.cashback.diamondsEquivalent', 'diamonds équivalent')}</div>
        </div>
        <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{t('admin.cashback.alreadyPaid', 'Déjà payés ce mois')}</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
            {kpiPaidCount}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#f97316', marginTop: '4px' }}>
            💎 {totalPaidDiamonds.toLocaleString(currentLocale)}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{t('admin.cashback.paymentsValidated', 'paiements validés')}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '10px', padding: '4px' }}>
        {[
          { key: 'live', label: t('admin.cashback.tabLive', 'Live'), count: liveCreators.length, countColor: '#22c55e' },
          { key: 'pending', label: t('admin.cashback.tabAllPending', 'Tous en attente'), count: kpiPendingCount, countColor: '#ef4444' },
          { key: 'history', label: t('admin.cashback.tabAllCreators', 'Tous les créateurs'), count: payments.length }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: '500',
              background: activeTab === tab.key ? '#1f1f1f' : 'transparent',
              color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span style={{
                background: tab.countColor, color: '#fff', borderRadius: '10px',
                padding: '1px 6px', fontSize: '10px', fontWeight: '700'
              }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* TAB: LIVE */}
      <div style={{ display: activeTab === 'live' ? 'block' : 'none' }}>
          {/* Vérifier Lives button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button
              onClick={checkLiveStatus}
              disabled={isChecking}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #22c55e',
                background: 'rgba(34,197,94,0.08)', color: '#22c55e', fontSize: '13px',
                fontWeight: '500', cursor: isChecking ? 'not-allowed' : 'pointer',
                opacity: isChecking ? 0.6 : 1, whiteSpace: 'nowrap'
              }}
            >
              {isChecking ? `⏳ ${t('admin.cashback.checking', 'Vérification...')}` : `📡 ${t('admin.cashback.checkLives', 'Vérifier Lives')}`}
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.loading', 'Chargement...')}</div>
          ) : liveCreators.length === 0 ? (
            <div style={{
              background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px',
              padding: '48px 24px', textAlign: 'center'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📡</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                {t('admin.cashback.noCreatorLive', 'Aucun créateur en live — Appuyez sur Vérifier Lives')}
              </div>
            </div>
          ) : (
            <div>
              {[...liveCreators].sort((a, b) => {
                // Group same creators together
                const keyA = a.tiktok_id || a.username || '';
                const keyB = b.tiktok_id || b.username || '';
                if (keyA !== keyB) return keyA.localeCompare(keyB);
                return (a.period || '').localeCompare(b.period || '');
              }).map((payment, idx, arr) => {
                const diamonds = Math.round((parseFloat(payment.cashback_amount) || 0) / 0.01);
                const tierColor = getTierColor(payment.tier_m1, payment.tier_current);
                const cleanUsername = (payment.username || '').replace(/^@/, '');
                const creatorKey = payment.tiktok_id || payment.username;
                const isMultiPeriod = multiPeriodCreators.has(creatorKey);
                const pColor = periodColorMap[payment.period] || '#8b5cf6';
                // Check if previous card is the same creator (to reduce margin for stacking)
                const prevKey = idx > 0 ? (arr[idx - 1].tiktok_id || arr[idx - 1].username) : null;
                const isStacked = prevKey === creatorKey;
                return (
                  <div
                    key={payment.id}
                    style={{
                      background: '#111',
                      border: isMultiPeriod ? `1px solid ${pColor}40` : '1px solid rgba(34,197,94,0.3)',
                      borderLeft: isMultiPeriod ? `3px solid ${pColor}` : undefined,
                      borderRadius: isStacked ? '0 0 12px 12px' : '12px',
                      borderTopLeftRadius: isStacked ? '0' : '12px',
                      borderTopRightRadius: isStacked ? '0' : '12px',
                      padding: '16px',
                      marginBottom: isStacked ? '12px' : '12px',
                      marginTop: isStacked ? '-12px' : '0'
                    }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div>
                        <div
                          style={{ fontSize: '16px', fontWeight: '700', color: '#fff', fontFamily: 'Sora, sans-serif', cursor: payment.tiktok_id ? 'pointer' : 'default' }}
                          onClick={() => payment.tiktok_id && navigate(`/${userRole === 'admin' ? 'admin' : 'manager'}/creators/${payment.tiktok_id}`)}
                        >
                          {payment.username || payment.tiktok_id || '—'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                          {payment.display_name && (
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                              {payment.display_name}
                            </span>
                          )}
                          <span style={{
                            fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '4px',
                            background: isMultiPeriod ? `${pColor}20` : '#1f1f1f',
                            color: isMultiPeriod ? pColor : 'rgba(255,255,255,0.5)',
                            border: isMultiPeriod ? `1px solid ${pColor}40` : 'none'
                          }}>
                            {formatPeriodShort(currentLocale,payment.period)}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                          {payment.whatsapp || '—'}
                        </div>
                      </div>
                      <span style={{
                        background: 'rgba(34,197,94,0.2)', color: '#22c55e',
                        padding: '4px 10px', borderRadius: '20px',
                        fontSize: '12px', fontWeight: '700',
                        animation: 'pulse 2s infinite'
                      }}>
                        ● LIVE
                      </span>
                    </div>

                    {/* Stats grid */}
                    {(() => {
                      const alreadyPaid = payment.amount_paid_diamonds || 0;
                      const remaining = diamonds - alreadyPaid;
                      const hasPartial = alreadyPaid > 0;
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: hasPartial ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                          <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>CASHBACK 💎</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#f97316' }}>
                              {diamonds.toLocaleString(currentLocale)}
                            </div>
                          </div>
                          {hasPartial && (
                            <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{t('admin.cashback.alreadyPaidLabel', 'ALREADY PAID')}</div>
                              <div style={{ fontSize: '15px', fontWeight: '700', color: '#22c55e' }}>
                                {alreadyPaid.toLocaleString(currentLocale)}
                              </div>
                            </div>
                          )}
                          <div style={{ background: hasPartial ? 'rgba(249,115,22,0.08)' : '#1a1a1a', border: hasPartial ? '1px solid rgba(249,115,22,0.2)' : 'none', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{hasPartial ? t('admin.cashback.remainingLabel', 'REMAINING') : 'CASHBACK $'}</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: hasPartial ? '#f97316' : '#22c55e' }}>
                              {hasPartial ? remaining.toLocaleString(currentLocale) : `$${parseFloat(payment.cashback_amount || 0).toFixed(2)}`}
                            </div>
                          </div>
                          <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>TIER</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: tierColor }}>
                              {payment.tier_m1 && payment.tier_current
                                ? `T${payment.tier_m1} → T${payment.tier_current}`
                                : '—'
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Rappel badge */}
                    {(payment.rappel_count || 0) > 0 && (
                      <div style={{
                        background: 'rgba(249,115,22,0.1)',
                        border: '1px solid rgba(249,115,22,0.3)',
                        borderRadius: '6px', padding: '6px 10px',
                        fontSize: '11px', color: '#f97316', marginBottom: '10px'
                      }}>
                        ⚠️ {t('admin.cashback.reminderCount', 'Rappel')} #{payment.rappel_count} — {t('admin.cashback.pendingSince', 'En attente depuis')} {formatPeriod(currentLocale,payment.period)}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <a
                        href={`https://www.tiktok.com/@${cleanUsername}/live`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          flex: 1, padding: '10px', borderRadius: '8px', textAlign: 'center',
                          background: 'rgba(0,0,0,0.3)', border: '1px solid #2a2a2a',
                          color: 'white', fontSize: '13px', fontWeight: '600',
                          textDecoration: 'none', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', gap: '6px'
                        }}
                      >
                        ▶ {t('admin.cashback.viewLive', 'Voir Live')}
                      </a>
                      <button
                        onClick={() => openValidationModal(payment)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '8px',
                          background: '#22c55e', border: 'none',
                          color: 'white', fontSize: '13px', fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        ✓ {t('admin.cashback.validate', 'Valider')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* TAB: TOUS EN ATTENTE */}
      <div style={{ display: activeTab === 'pending' ? 'block' : 'none' }}>
          {/* Controls row */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <StableSearchInput onChange={setSearchQuery} placeholder={t('admin.cashback.searchPlaceholder', 'Rechercher username, display name, TikTok ID, WhatsApp...')} />
            <button
              onClick={checkLiveStatus}
              disabled={isChecking}
              style={{
                padding: '9px 16px', borderRadius: '8px', border: '1px solid #22c55e',
                background: 'rgba(34,197,94,0.08)', color: '#22c55e', fontSize: '13px',
                fontWeight: '500', cursor: isChecking ? 'not-allowed' : 'pointer',
                opacity: isChecking ? 0.6 : 1, whiteSpace: 'nowrap'
              }}
            >
              {isChecking ? `⏳ ${t('admin.cashback.checking', 'Vérification...')}` : `📡 ${t('admin.cashback.checkLives', 'Vérifier Lives')}`}
            </button>
            <select
              value={periodFilter}
              onChange={e => setPeriodFilter(e.target.value)}
              style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '16px', outline: 'none' }}
            >
              <option value="">{t('admin.cashback.allPeriods', 'Toutes les périodes')}</option>
              {periods.map(p => <option key={p} value={p}>{formatPeriod(currentLocale,p)}</option>)}
            </select>
            <button
              onClick={() => setHideDeparted(v => !v)}
              style={{
                padding: '9px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                border: `1px solid ${hideDeparted ? '#ef4444' : '#1f1f1f'}`,
                background: hideDeparted ? 'rgba(239,68,68,0.08)' : '#111',
                color: hideDeparted ? '#ef4444' : 'rgba(255,255,255,0.5)',
                whiteSpace: 'nowrap',
              }}
            >
              {hideDeparted ? `${t('admin.cashback.leftHidden', 'Left hidden')} (${departedCount})` : `${t('admin.cashback.showLeft', 'Show left')} (${departedCount})`}
            </button>
          </div>

          {/* Table */}
          <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                    {['USERNAME', 'DISPLAY NAME', 'WHATSAPP', t('admin.cashback.periodHeader', 'PERIOD'), 'DIAMONDS', 'CASHBACK 💎', t('admin.cashback.alreadyPaidLabel', 'ALREADY PAID'), t('admin.cashback.remainingLabel', 'REMAINING'), 'TIER', 'LIVE', t('admin.cashback.remindersHeader', 'REMINDERS'), t('admin.cashback.actionsHeader', 'ACTIONS')].map(h => (
                      <th key={h} style={{ padding: '10px 10px', textAlign: 'left', color: 'rgba(255,255,255,0.35)', fontWeight: '500', fontSize: '11px', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.loading', 'Chargement...')}</td></tr>
                  ) : sortedPending.length === 0 ? (
                    <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.noPendingPayment', 'Aucun paiement en attente')}</td></tr>
                  ) : sortedPending.map(p => {
                    const diamonds = Math.round((parseFloat(p.cashback_amount) || 0) / 0.01);
                    const tierColor = getTierColor(p.tier_m1, p.tier_current);
                    const creatorKey = p.tiktok_id || p.username;
                    const isMultiPeriod = multiPeriodCreators.has(creatorKey);
                    const pColor = periodColorMap[p.period] || '#8b5cf6';
                    return (
                      <tr
                        key={p.id}
                        style={{
                          borderBottom: '1px solid #1a1a1a',
                          background: p.is_departed ? 'rgba(239,68,68,0.06)' : p.is_live ? 'rgba(34,197,94,0.04)' : 'transparent',
                          borderLeft: isMultiPeriod ? `3px solid ${pColor}` : undefined,
                        }}
                      >
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: p.is_departed ? '#ef4444' : (p.rappel_count || 0) > 0 ? '#ef4444' : '#fff', whiteSpace: 'nowrap', cursor: p.tiktok_id ? 'pointer' : 'default' }}
                          onClick={() => p.tiktok_id && navigate(`/${userRole === 'admin' ? 'admin' : 'manager'}/creators/${p.tiktok_id}`)}
                        >
                          {p.username || p.tiktok_id || '—'}
                          {p.is_departed && <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>PARTI</span>}
                          {isMultiPeriod && <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: `${pColor}20`, color: pColor }}>MULTI</span>}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                          {p.display_name || '—'}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.5)' }}>{p.whatsapp || '—'}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            background: isMultiPeriod ? `${pColor}20` : '#1f1f1f',
                            color: isMultiPeriod ? pColor : 'rgba(255,255,255,0.7)',
                            border: isMultiPeriod ? `1px solid ${pColor}40` : 'none',
                            borderRadius: '6px', padding: '3px 8px', fontSize: '11px', whiteSpace: 'nowrap', fontWeight: isMultiPeriod ? '700' : '400'
                          }}>
                            {formatPeriod(currentLocale,p.period)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.7)' }}>{formatNumber(currentLocale,p.diamonds)}</td>
                        <td style={{ padding: '12px 14px', color: '#f97316', fontWeight: '600' }}>💎 {formatNumber(currentLocale,diamonds)}</td>
                        <td style={{ padding: '12px 14px', color: (p.amount_paid_diamonds || 0) > 0 ? '#22c55e' : 'rgba(255,255,255,0.25)', fontWeight: '600' }}>
                          {(p.amount_paid_diamonds || 0) > 0 ? `💎 ${formatNumber(currentLocale,p.amount_paid_diamonds)}` : '—'}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#f97316', fontWeight: '700' }}>
                          💎 {formatNumber(currentLocale,diamonds - (p.amount_paid_diamonds || 0))}
                        </td>
                        <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                          {p.tier_m1 && p.tier_current ? (
                            <span style={{ color: tierColor, fontWeight: '600' }}>T{p.tier_m1} → T{p.tier_current}</span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {p.is_live ? (
                            <span style={{ color: '#22c55e', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                              LIVE
                            </span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)' }}>○</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {(p.rappel_count || 0) > 0 ? (
                            <span style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '600' }}>
                              {p.rappel_count}
                            </span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <button
                            onClick={() => openValidationModal(p)}
                            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #22c55e', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                          >
                            ✓ {t('admin.cashback.validate', 'Valider')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
      </div>

      {/* TAB: TOUS LES CRÉATEURS */}
      <div style={{ display: activeTab === 'history' ? 'block' : 'none' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <StableSearchInput onChange={setAllSearchQuery} placeholder={t('admin.cashback.searchCreator', 'Search a creator...')} />
            <div style={{ display: 'flex', gap: '4px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '8px', padding: '3px' }}>
              {[
                { key: '', label: t('admin.cashback.allCreators', 'All creators'), color: '#fff' },
                { key: 'paid', label: t('admin.cashback.statusPaid', 'Paid'), color: '#22c55e' },
                { key: 'pending', label: t('admin.cashback.statusNotPaid', 'Not paid'), color: '#ef4444' },
                { key: 'partial', label: t('admin.cashback.statusPartial', 'Partial'), color: '#f97316' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  style={{
                    padding: '7px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    fontSize: '11px', fontWeight: '600',
                    background: statusFilter === f.key ? (f.key ? f.color + '20' : '#1f1f1f') : 'transparent',
                    color: statusFilter === f.key ? f.color : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.15s'
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '4px', background: '#111', border: '1px solid #1f1f1f', borderRadius: '8px', padding: '3px' }}>
              {[
                { key: 'period', label: t('admin.cashback.byPeriod', 'Par période') },
                { key: 'creator', label: t('admin.cashback.byCreator', 'Par créateur') }
              ].map(v => (
                <button
                  key={v.key}
                  onClick={() => setHistoryView(v.key)}
                  style={{
                    padding: '7px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    fontSize: '12px', fontWeight: '500',
                    background: historyView === v.key ? '#1f1f1f' : 'transparent',
                    color: historyView === v.key ? '#fff' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.15s'
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <select
              value={historyPeriodFilter}
              onChange={e => setHistoryPeriodFilter(e.target.value)}
              style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '16px', outline: 'none' }}
            >
              <option value="">{t('admin.cashback.allPeriods', 'Toutes les périodes')}</option>
              {periods.map(p => <option key={p} value={p}>{formatPeriod(currentLocale,p)}</option>)}
            </select>
          </div>

          {historyView === 'period' && (
            <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.cashback.periodHeader', 'PERIOD'), t('admin.cashback.historyCreators', 'CREATORS'), 'TOTAL $', 'TOTAL 💎', t('admin.cashback.historyPctPaid', '% PAID'), t('admin.cashback.historyStatus', 'STATUS'), 'ACTION'].map(h => (
                        <th key={h} style={{ padding: '10px 10px', textAlign: 'left', color: 'rgba(255,255,255,0.35)', fontWeight: '500', fontSize: '11px', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.loading', 'Chargement...')}</td></tr>
                    ) : historyRows.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.noHistory', 'Aucun historique disponible')}</td></tr>
                    ) : historyRows.map(row => {
                      const totalDiamonds = Math.round(row.totalAmount / 0.01);
                      const pctPaid = row.total > 0 ? Math.round((row.paidCount / row.total) * 100) : 0;
                      const isComplete = row.paidCount === row.total && row.total > 0;
                      return (
                        <tr key={row.period} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ background: '#1f1f1f', color: '#fff', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: '600' }}>
                              {formatPeriod(currentLocale,row.period)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.7)' }}>{row.total}</td>
                          <td style={{ padding: '12px 14px', color: '#22c55e', fontWeight: '600' }}>
                            ${row.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#f97316', fontWeight: '600' }}>💎 {formatNumber(currentLocale,totalDiamonds)}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, height: '4px', background: '#1f1f1f', borderRadius: '2px', minWidth: '50px' }}>
                                <div style={{ width: `${pctPaid}%`, height: '100%', background: isComplete ? '#22c55e' : '#f97316', borderRadius: '2px' }} />
                              </div>
                              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', whiteSpace: 'nowrap' }}>{pctPaid}%</span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {periodStatuses[row.period] === 'on_hold' ? (
                              <span style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.statusOnHold', 'On hold')}</span>
                            ) : isComplete ? (
                              <span style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.historyComplete', 'Complet ✓')}</span>
                            ) : (
                              <span style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.historyInProgress', 'En cours')}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {isAdmin && (
                              <button
                                onClick={() => togglePeriodStatus(row.period, periodStatuses[row.period] || 'active')}
                                disabled={togglingPeriod === row.period}
                                style={{
                                  padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                  cursor: togglingPeriod === row.period ? 'not-allowed' : 'pointer',
                                  border: periodStatuses[row.period] === 'on_hold'
                                    ? '1px solid #22c55e' : '1px solid #ef4444',
                                  background: periodStatuses[row.period] === 'on_hold'
                                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                                  color: periodStatuses[row.period] === 'on_hold'
                                    ? '#22c55e' : '#ef4444',
                                  opacity: togglingPeriod === row.period ? 0.5 : 1,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {periodStatuses[row.period] === 'on_hold' ? 'Activer' : 'Suspendre'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {historyView === 'creator' && (
            <div style={{ background: '#111', border: '1px solid #1f1f1f', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {['USERNAME', 'DISPLAY NAME', t('admin.cashback.periodHeader', 'PERIOD'), t('admin.cashback.historyStatus', 'STATUS'), 'CASHBACK 💎', t('admin.cashback.paidDiamondsHeader', 'PAID 💎'), t('admin.cashback.remainingLabel', 'REMAINING'), 'TIER', t('admin.cashback.paidBy', 'PAID BY'), 'DATE'].map(h => (
                        <th key={h} style={{ padding: '10px 10px', textAlign: 'left', color: 'rgba(255,255,255,0.35)', fontWeight: '500', fontSize: '11px', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.loading', 'Chargement...')}</td></tr>
                    ) : filteredAllPayments.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>{t('admin.cashback.noCreatorFound', 'No creator found')}</td></tr>
                    ) : filteredAllPayments.map(p => {
                      const isPaid = p.status === 'paid';
                      const diamonds = Math.round((parseFloat(p.cashback_amount) || 0) / 0.01);
                      const paidDiamonds = p.amount_paid_diamonds || (isPaid ? diamonds : 0);
                      const remaining = isPaid ? 0 : diamonds - paidDiamonds;
                      const tierColor = getTierColor(p.tier_m1, p.tier_current);
                      const isPartial = !isPaid && paidDiamonds > 0;
                      const isOnHold = periodStatuses[p.period] === 'on_hold';
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #1a1a1a', background: p.is_departed ? 'rgba(239,68,68,0.06)' : isPaid ? 'rgba(34,197,94,0.03)' : 'transparent' }}>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', cursor: p.tiktok_id ? 'pointer' : 'default' }}
                            onClick={() => p.tiktok_id && navigate(`/${userRole === 'admin' ? 'admin' : 'manager'}/creators/${p.tiktok_id}`)}
                          >
                            <div style={{ fontWeight: '700', color: p.is_departed ? '#ef4444' : '#fff' }}>
                              {p.username || p.tiktok_id || '—'}
                              {p.is_departed && <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>PARTI</span>}
                            </div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>{p.display_name || '—'}</div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ background: '#1f1f1f', color: 'rgba(255,255,255,0.7)', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', whiteSpace: 'nowrap' }}>
                              {formatPeriodShort(currentLocale,p.period)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {isPaid ? (
                              <span style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.statusPaid', 'Paid')}</span>
                            ) : isOnHold ? (
                              <span style={{ background: 'rgba(107,114,128,0.12)', color: '#6b7280', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.statusOnHold', 'On hold')}</span>
                            ) : isPartial ? (
                              <span style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.statusPartial', 'Partial')}</span>
                            ) : (
                              <span style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: '600' }}>{t('admin.cashback.statusNotPaid', 'Not paid')}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#f97316', fontWeight: '600' }}>
                            💎 {formatNumber(currentLocale,diamonds)}
                          </td>
                          <td style={{ padding: '12px 14px', color: paidDiamonds > 0 ? '#22c55e' : 'rgba(255,255,255,0.25)', fontWeight: '600' }}>
                            {paidDiamonds > 0 ? `💎 ${formatNumber(currentLocale,paidDiamonds)}` : '—'}
                          </td>
                          <td style={{ padding: '12px 14px', color: remaining > 0 ? '#ef4444' : 'rgba(255,255,255,0.25)', fontWeight: '600' }}>
                            {remaining > 0 ? `💎 ${formatNumber(currentLocale,remaining)}` : isPaid ? '✓' : '—'}
                          </td>
                          <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                            {p.tier_m1 && p.tier_current ? (
                              <span style={{ color: tierColor, fontWeight: '600' }}>T{p.tier_m1} → T{p.tier_current}</span>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                            {p.paid_by_name || '—'}
                          </td>
                          <td style={{ padding: '12px 14px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {p.paid_at ? formatDate(p.paid_at) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>

      {/* MODAL VALIDATION */}
      {validationModal && (() => {
        const pm = validationModal;
        const suggestions = generateSuggestions(pm._remaining);
        const inputVal = parseInt(validationAmount) || 0;
        const isPartial = inputVal > 0 && inputVal < pm._remaining;
        const isOver = inputVal > pm._remaining;
        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
          }} onClick={() => setValidationModal(null)}>
            <div style={{
              background: '#141414', border: '1px solid #2a2a2a', borderRadius: '16px',
              padding: '28px', width: '100%', maxWidth: '420px', position: 'relative'
            }} onClick={e => e.stopPropagation()}>
              {/* Close */}
              <button onClick={() => setValidationModal(null)} style={{
                position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.4)', fontSize: '20px', cursor: 'pointer'
              }}>✕</button>

              {/* Header */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff', fontFamily: 'Sora, sans-serif' }}>
                  {t('admin.cashback.validatePayment', 'Validate payment')}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                  {pm.username || pm.tiktok_id} {pm.display_name ? `(${pm.display_name})` : ''}
                </div>
              </div>

              {/* Recap */}
              <div style={{ background: '#1a1a1a', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{t('admin.cashback.totalCashback', 'Total cashback')}</span>
                  <span style={{ color: '#f97316', fontWeight: '700', fontSize: '14px' }}>💎 {formatNumber(currentLocale,pm._cashbackDiamonds)}</span>
                </div>
                {pm._alreadyPaid > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{t('admin.cashback.alreadyPaidShort', 'Already paid')}</span>
                      <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '14px' }}>💎 {formatNumber(currentLocale,pm._alreadyPaid)}</span>
                    </div>
                    <div style={{ height: '1px', background: '#2a2a2a', margin: '8px 0' }} />
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{t('admin.cashback.remainingToPay', 'Remaining to pay')}</span>
                  <span style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>💎 {formatNumber(currentLocale,pm._remaining)}</span>
                </div>
                {/* Historique paiements partiels */}
                {Array.isArray(pm.payment_history) && pm.payment_history.length > 0 && (
                  <div style={{ marginTop: '12px', borderTop: '1px solid #2a2a2a', paddingTop: '10px' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{t('admin.cashback.previousTransactions', 'Previous transactions')}</div>
                    {pm.payment_history.map((tx, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px' }}>
                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>{formatDate(tx.paid_at)} — {tx.paid_by_name}</span>
                        <span style={{ color: '#22c55e', fontWeight: '600' }}>💎 {formatNumber(currentLocale,tx.amount_diamonds)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Input montant */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '6px' }}>
                  {t('admin.cashback.amountToGive', 'Amount to give (in diamonds)')}
                </label>
                <input
                  type="number"
                  value={validationAmount}
                  onChange={e => setValidationAmount(e.target.value)}
                  style={{
                    width: '100%', background: '#111', border: '1px solid #2a2a2a', borderRadius: '10px',
                    padding: '14px', color: '#fff', fontSize: '20px', fontWeight: '700',
                    textAlign: 'center', outline: 'none', fontFamily: 'Sora, sans-serif'
                  }}
                  autoFocus
                />
              </div>

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {suggestions.map(s => (
                    <button
                      key={s}
                      onClick={() => setValidationAmount(String(s))}
                      style={{
                        padding: '6px 14px', borderRadius: '8px',
                        border: String(s) === validationAmount ? '1px solid #22c55e' : '1px solid #2a2a2a',
                        background: String(s) === validationAmount ? 'rgba(34,197,94,0.1)' : '#1a1a1a',
                        color: String(s) === validationAmount ? '#22c55e' : 'rgba(255,255,255,0.6)',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                      }}
                    >
                      💎 {formatNumber(currentLocale,s)}
                    </button>
                  ))}
                </div>
              )}

              {/* Info partiel/arrondi */}
              {isPartial && (
                <div style={{
                  background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)',
                  borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
                  fontSize: '12px', color: '#f97316'
                }}>
                  {t('admin.cashback.partialPaymentInfo', 'Partial payment — Remaining')} 💎 {formatNumber(currentLocale,pm._remaining - inputVal)} {t('admin.cashback.willRemainPending', 'will remain pending')}
                </div>
              )}
              {isOver && (
                <div style={{
                  background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)',
                  borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
                  fontSize: '12px', color: '#22c55e'
                }}>
                  {t('admin.cashback.roundedInFavor', 'Rounded in favor of creator')} (+💎 {formatNumber(currentLocale,inputVal - pm._remaining)})
                </div>
              )}

              {/* Bouton valider */}
              <button
                onClick={confirmValidation}
                disabled={inputVal <= 0}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
                  background: inputVal > 0 ? '#22c55e' : '#2a2a2a',
                  color: inputVal > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
                  fontSize: '15px', fontWeight: '700', cursor: inputVal > 0 ? 'pointer' : 'not-allowed',
                  fontFamily: 'Sora, sans-serif'
                }}
              >
                {isPartial
                  ? `${t('admin.cashback.validate', 'Validate')} ${formatNumber(currentLocale,inputVal)} 💎 (${t('admin.cashback.statusPartial', 'Partial').toLowerCase()})`
                  : `${t('admin.cashback.validate', 'Validate')} ${formatNumber(currentLocale,inputVal)} 💎`
                }
              </button>
            </div>
          </div>
        );
      })()}

      {/* Toast */}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', background: '#1f1f1f', border: '1px solid #2a2a2a',
          color: '#fff', padding: '12px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '500',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 9999, maxWidth: '360px'
        }}>
          {toastMsg}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </main>
  );

  return isAdmin ? (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} userEmail={userEmail} />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
        <AdminMobileHeader title="Paiements Cashback" />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {cashbackContent}
        </div>
      </div>
      <AdminBottomNav />
    </div>
  ) : (
    <div>
      {/* Desktop: sidebar left */}
      <div className="hidden md:flex" style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)' }}>
        <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} managerName={currentUserName} />
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {cashbackContent}
        </div>
      </div>
      {/* Mobile: top header + bottom nav */}
      <div className="md:hidden" style={{ backgroundColor: 'var(--color-background)', minHeight: '100vh' }}>
        <ManagerMobileHeader title="Paiements Cashback" />
        <div style={{ paddingBottom: '80px' }}>
          {cashbackContent}
        </div>
        <ManagerBottomNav />
      </div>
    </div>
  );
};

export default CashbackPayments;
